import fp from "lodash/fp";

import { getFullName } from "helpers";
import {
	PayoutMethodTypes,
	ScenarioTypes,
	SourceFileTypeWithName,
} from "types";
import { float2Normalizer } from "validators";

export const StatusValues = [
	{ value: true, name: "Available" },
	{ value: false, name: "Unavailable" },
];

export const ScheduleAStatus = {
	UNAVAILABLE: 0,
	AVAILABLE: 1,
};

export const StatusMethodTypes = {
	INACTIVE: 0,
	ACTIVE: 1,
};

export const LimitTypes = {
	REDUCED_RESIDUAL_SPLIT: 0,
	TIME_LIMIT_AGENT: 1,
	TIME_LIMIT_MERCHANT: 2,
};

export const LimitValues = [
	{
		value: LimitTypes.REDUCED_RESIDUAL_SPLIT,
		name: "Reduced Residual Split",
	},
	{
		value: LimitTypes.TIME_LIMIT_AGENT,
		name: "Time Limit Agent Start Date",
	},
	{
		value: LimitTypes.TIME_LIMIT_MERCHANT,
		name: "Time Limit Merchant Start Date",
	},
];

export const TriggerTypes = {
	MINIMUM_MONTHLY_VOLUME: 0,
	REDUCED_RESIDUAL_SPLIT: 1,
};

export const TriggerValues = [
	{
		value: TriggerTypes.MINIMUM_MONTHLY_VOLUME,
		name: "Minimum Monthly Volume",
	},
	{
		value: TriggerTypes.REDUCED_RESIDUAL_SPLIT,
		name: "Reduced Residual Split",
	},
];

export const isHighRiskValue = (highRisk: string | undefined) => {
	return highRisk === "true" ? "Yes" : "No";
};

export const entityTypes = {
	AGENT: 0,
	HOUSE_ACCOUNT: 1,
};

export const partnerTypes = {
	OUTSIDE_SALES_AGENT: 0,
	OUTSIDE_SALES_MANAGER: 1,
	OUTSIDE_SALES_OFFICE: 2,
	INSIDE_SALES_AGENT: 3,
	INSIDE_SALES_MANAGER: 4,
	BUSINESS_DEVELOPMENT: 5,
	ASSOCIATION: 6,
	REFERRAL_SOURCE: 7,
	ISV: 8,
	HOUSE_ACCOUNT: 9,
	AURORA_HOUSE_ACCOUNT: 15,
};

export function getEntityTypeName(type) {
	let name = "";
	switch (type) {
		case entityTypes.AGENT:
			name = "Agent";
			break;
		case entityTypes.HOUSE_ACCOUNT:
			name = "House Account";
			break;
		default:
			break;
	}
	return name;
}

export const accountTypes = {
	SAVINGS: 0,
	CHEKING: 1,
};

export function getAccountTypeName(type) {
	let name = "";
	switch (type) {
		case accountTypes.SAVINGS:
			name = "Savings";
			break;
		case accountTypes.CHEKING:
			name = "Checking";
			break;
		default:
			break;
	}
	return name;
}

export const withholdReasons = {
	None: 0,
	ProspectiveAgentPartner: 1,
	TerminatedAgentPartner: 2,
	RiskRequest: 3,
	LegalRequest: 4,
	Other: 5,
	MissingPayoutDetails: 6,
};

export type SplitInformationType = {
	paymentsLimitType: string;
	paymentTrigger: string;
	agent?: {
		id: number;
		firstName: string;
		lastName: string;
		name: string;
		entityType: number;
		partnerType: number;
	};
	payingDurationMonths: string;
	reducedPercentLimitReached: string;
	volume: string;
	reducedPercentMinVolumeNotReached: string;
	splitPercent: string;
	schedule_a_id: string;
	isUseSecondaryAgentScheduleForCalculations: boolean;
	calculation: {
		agentId: string | null;
		payoutAmountBeforeAdjustments: number;
		residualsAmountBeforeSplit: number;
	};
	isReadOnlySplitPercent: boolean;
	isWaterfallSplitPercentWarning: boolean;
};

export const defaultSplitInformation: SplitInformationType = {
	paymentsLimitType: "",
	paymentTrigger: "",
	payingDurationMonths: "",
	reducedPercentLimitReached: "",
	volume: "",
	reducedPercentMinVolumeNotReached: "",
	splitPercent: "",
	schedule_a_id: "",
	isUseSecondaryAgentScheduleForCalculations: false,
	calculation: {
		agentId: null,
		payoutAmountBeforeAdjustments: 0,
		residualsAmountBeforeSplit: 0,
	},
	isReadOnlySplitPercent: false,
	isWaterfallSplitPercentWarning: false,
};

export const paymentInfoMapper = (
	paymentInfo,
	businessUnit,
	businessUnitHierarchy,
) => {
	if (!paymentInfo) {
		return {
			payment_info: {},
		};
	}

	const payoutMethodType = parseInt(paymentInfo?.payment_method_type);

	let paymentMethod = {};
	let payeeName = null;

	switch (payoutMethodType) {
		case PayoutMethodTypes.ACH.value:
			payeeName = paymentInfo?.ach?.payee_name;
			paymentMethod = { ach: paymentInfo?.ach };
			break;
		case PayoutMethodTypes.DomesticWire.value:
			payeeName = paymentInfo?.domestic_wire?.payee_name;
			paymentMethod = {
				domestic_wire: paymentInfo?.domestic_wire,
			};
			break;
		case PayoutMethodTypes.InternationalWire.value:
			payeeName = paymentInfo?.international_wire?.payee_name;
			paymentMethod = {
				international_wire: paymentInfo?.international_wire,
			};
			break;
		case PayoutMethodTypes.Check.value:
			payeeName = paymentInfo?.check.payee_name;
			paymentMethod = {
				check: paymentInfo?.check,
			};
			break;
		case PayoutMethodTypes.Payroll.value:
			paymentMethod = {
				payroll: {},
			};
			break;
		default:
			break;
	}
	return {
		payment_info: {
			business_unit: !businessUnit ? 0 : businessUnit.name,
			business_unit_hierarchy: !businessUnitHierarchy
				? 0
				: businessUnitHierarchy.name,
			residuals_status: paymentInfo?.residuals_status,
			withhold_reason:
				parseInt(paymentInfo?.withhold_reason) === withholdReasons?.None
					? null
					: paymentInfo?.withhold_reason?.toString(),
			number: paymentInfo.number,
			payee_name: payeeName,
			...paymentMethod,
		},
	};
};

export function convertSplitProfileToFormValues(data) {
	let values = {
		id: data.id,
		userFriendlyId: getSplitProfileFriendlyId(data),
		profileName: data.name,
		scenarioType: ScenarioTypes.getByValue(data.scenario),
		status: StatusValues[data.is_available ? 0 : 1],
		isHighRisk: data.is_high_risk.toString(),
		isDefault: data.is_default || false,
		totalRevenueShare: data.total_revenue_share,
		primarySplitInformation: {
			agentId: data.primary_split_information.agent_id,
			agent: {
				id: data.primary_split_information.agent_id,
				firstName: data.primary_split_information.agent.first_name,
				lastName: data.primary_split_information.agent.last_name,
				name: `${getFullName(data.primary_split_information.agent)} ${
					data.primary_split_information.agent.is_active
						? ""
						: "(inactive)"
				}`,
				entityType: data.primary_split_information.agent.entity_type,
				partnerType: data.primary_split_information.agent.partner_type,
				partnerTypeDisplayName:
					data.primary_split_information.agent
						.partner_type_display_name,
			},
			paymentsLimitType:
				LimitValues[data.primary_split_information.payments_limit_type],
			payingDurationMonths:
				data.primary_split_information.paying_duration_months,
			reducedPercentLimitReached:
				data.primary_split_information.reduced_percent_limit_reached,
			reducedPercentMinVolumeNotReached:
				data.primary_split_information
					.reduced_percent_min_volume_not_reached,
			paymentTrigger:
				TriggerValues[data.primary_split_information.payment_trigger],
			volume: float2Normalizer(data.primary_split_information.volume),
			splitPercent: data.primary_split_information.split_percent,
			entityType: data.primary_split_information.entity_type,
		},
		secondarySplitInformation: data.secondary_split_information.map(s => ({
			agentId: s.agent_id,
			agent: {
				id: s.agent_id,
				firstName: s.agent.first_name,
				lastName: s.agent.last_name,
				name: `${getFullName(s.agent)} ${
					s.agent.is_active ? "" : "(inactive)"
				}`,
				entityType: s.agent.entity_type,
				partnerType: s.agent.partner_type,
				partnerTypeDisplayName: s.agent.partner_type_display_name,
			},
			paymentsLimitType: LimitValues[s.payments_limit_type],
			payingDurationMonths: s.paying_duration_months,
			reducedPercentLimitReached: s.reduced_percent_limit_reached,
			reducedPercentMinVolumeNotReached:
				s.reduced_percent_min_volume_not_reached,
			paymentTrigger: TriggerValues[s.payment_trigger],
			volume: float2Normalizer(s.volume),
			splitPercent: s.split_percent,
			entityType: s.entity_type,
			schedule_a_id: s.schedule_a_id,
			scheduleAName: s.schedule_a_name,
			isUseSecondaryAgentScheduleForCalculations:
				s.schedule_a_id === null ? "false" : "true",
			isPrivate: s.is_private,
		})),
	};
	return values;
}

export function convertFormToSplitProfile(form) {
	let payload = {
		id: form.id,
		name: form.profileName,
		scenario: form.scenarioType.value,
		is_available: form.status.value,
		is_high_risk: form.isHighRisk,
		is_default: form.isDefault,
		total_revenue_share: form.totalRevenueShare,
		primary_split_information: {
			agent_id: form.primarySplitInformation.agent.id,
			payments_limit_type:
				form.primarySplitInformation.paymentsLimitType?.value,
			paying_duration_months:
				form.primarySplitInformation.payingDurationMonths,
			reduced_percent_limit_reached:
				form.primarySplitInformation.reducedPercentLimitReached,
			reduced_percent_min_volume_not_reached:
				form.primarySplitInformation.reducedPercentMinVolumeNotReached,
			payment_trigger: form.primarySplitInformation.paymentTrigger?.value,
			volume: form.primarySplitInformation.volume,
			split_percent: form.primarySplitInformation.splitPercent,
		},
		secondary_split_information:
			form.secondarySplitInformation?.map(s => ({
				agent_id: s.agent.id,
				payments_limit_type: s.paymentsLimitType?.value,
				paying_duration_months: s.payingDurationMonths,
				reduced_percent_limit_reached: s.reducedPercentLimitReached,
				reduced_percent_min_volume_not_reached:
					s.reducedPercentMinVolumeNotReached,
				payment_trigger: s.paymentTrigger?.value,
				volume: s.volume,
				split_percent: s.splitPercent,
				schedule_a_id: s.schedule_a_id?.id,
				is_private: s.isPrivate,
			})) || [],
	};
	return payload;
}

export const setInitialPaymentInfoData = (
	paymentInfo,
	defaultPayeeName,
	address,
) => {
	const payeeName = paymentInfo?.payee_name
		? paymentInfo?.payee_name
		: defaultPayeeName;

	let withholdReasonStatus = {
		withhold_reason: paymentInfo?.withhold_reason?.toString(),
		residuals_status: paymentInfo?.residuals_status?.toString(),
	};

	const getValue = (unmaskValue, maskValue) =>
		unmaskValue ? unmaskValue : maskValue ?? "";

	return {
		payment_info: {
			number: paymentInfo?.number,
			payment_method_type: paymentInfo?.payment_method_type?.toString(),
			residuals_status_updated_by:
				paymentInfo?.residuals_status_updated_by,
			...withholdReasonStatus,
			ach: {
				...paymentInfo?.ach,
				payee_name: payeeName,
				account_type: paymentInfo?.ach?.account_type?.toString(),
				dda: getValue(
					paymentInfo?.ach?.dda,
					paymentInfo?.ach?.dda_mask,
				),
				aba: getValue(
					paymentInfo?.ach?.aba,
					paymentInfo?.ach?.aba_mask,
				),
			},
			domestic_wire: {
				...paymentInfo?.domestic_wire,
				payee_name: payeeName,
				account_type:
					paymentInfo?.domestic_wire?.account_type?.toString(),
				dda: getValue(
					paymentInfo?.domestic_wire?.dda,
					paymentInfo?.domestic_wire?.dda_mask,
				),
				aba: getValue(
					paymentInfo?.domestic_wire?.aba,
					paymentInfo?.domestic_wire?.aba_mask,
				),
			},
			international_wire: {
				...paymentInfo?.international_wire,
				payee_name: payeeName,
				swift_bic: getValue(
					paymentInfo?.international_wire?.swift_bic,
					paymentInfo?.international_wire?.swift_bic_mask,
				),
				iban: getValue(
					paymentInfo?.international_wire?.iban,
					paymentInfo?.international_wire?.iban_mask,
				),
			},
			check: {
				payee_name: payeeName,
				address,
				...paymentInfo?.check,
			},
		},
	};
};

export function getScheduleAFriendlyId(scheduleA) {
	return `SA-${scheduleA?.user_friendly_id}`;
}

export function getSplitProfileFriendlyId(profile) {
	return `RS-${profile?.user_friendly_id}`;
}

export function getSplitProfileFriendlyIdById(id) {
	if (!id) return "empty";
	return `RS-${id}`;
}

export function getScheduleAidFriendlyIdById(id) {
	if (!id) return "empty";
	return `SA-${id}`;
}

export const groupFilesByProcessor = (items: SourceFileTypeWithName[]): any => {
	if (!items) return;
	const groupByProcessor = items.reduce(
		(obj, { source_type_name, ...item }) => {
			obj[source_type_name] = obj[source_type_name] || [];
			obj[source_type_name].push(item);
			return obj;
		},
		{},
	);

	const sourceListResult = Object.entries(groupByProcessor)
		.map(([source_type_name, items]) => ({
			source_type_name,
			items,
		}))
		.sort((firstSource, nextSource) =>
			firstSource.source_type_name > nextSource.source_type_name ? 1 : -1,
		);

	return sourceListResult;
};

export const periodVarianceTypes = {
	PREVIOUS_MONTH: "previous_month",
	TWELVE_MONTH_AVERAGE: "twelve_month_average",
	PREVIOUS_YEAR: "previous_year",
};

export const periodVarianceValues = [
	{
		value: periodVarianceTypes.PREVIOUS_MONTH,
		name: "Previous month",
	},
	{
		value: periodVarianceTypes.TWELVE_MONTH_AVERAGE,
		name: "12 month average",
	},
	{
		value: periodVarianceTypes.PREVIOUS_YEAR,
		name: "Previous year",
	},
];

export function volumeVarianceAccessor(value) {
	switch (value) {
		case periodVarianceTypes.PREVIOUS_MONTH:
			return "total_volumes_previous_month_variance";
		case periodVarianceTypes.TWELVE_MONTH_AVERAGE:
			return "total_volumes_previous_year_avg_variance";
		case periodVarianceTypes.PREVIOUS_YEAR:
			return "total_volumes_previous_year_variance";
		default:
			break;
	}
}

export function payoutVarianceAccessor(value) {
	switch (value) {
		case periodVarianceTypes.PREVIOUS_MONTH:
			return "payout_previous_month_variance";
		case periodVarianceTypes.TWELVE_MONTH_AVERAGE:
			return "payout_previous_year_avg_variance";
		case periodVarianceTypes.PREVIOUS_YEAR:
			return "payout_previous_year_variance";
		default:
			break;
	}
}

export function payoutVarianceResidualsPaymentsAccessor(value) {
	switch (value) {
		case periodVarianceTypes.PREVIOUS_MONTH:
			return "total_payout_amount_previous_month_variance";
		case periodVarianceTypes.TWELVE_MONTH_AVERAGE:
			return "total_payout_amount_previous_year_avg_variance";
		case periodVarianceTypes.PREVIOUS_YEAR:
			return "total_payout_amount_previous_year_variance";
		default:
			break;
	}
}

export const isPercentDifferenceExceeded = (amount, variance, maxPercent) => {
	if (amount - variance === 0.0) {
		return variance === 0.0 ? false : true;
	}

	const percent = Math.abs((variance / (amount - variance)) * 100.0);

	return percent > maxPercent;
};

export const filterScheduleAsAccordingProcessor = (scheduleAs, processorId) => {
	const scheduleAWithSpecificProcessor = scheduleAs?.filter(
		scheduleA => scheduleA?.processor_id === parseInt(processorId),
	);

	const scheduleAWithNotProcessor = scheduleAs?.filter(
		scheduleA => scheduleA?.processor_id === null,
	);

	const value = () => {
		if (!fp.isEmpty(scheduleAWithSpecificProcessor)) {
			return scheduleAWithSpecificProcessor;
		} else if (!fp.isEmpty(scheduleAWithNotProcessor)) {
			return scheduleAWithNotProcessor;
		} else {
			return [];
		}
	};

	return value();
};
